import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { Drawer, IconButton, List, ListItem, ListItemText, Divider, Collapse } from '@mui/material';
import Logo from '../../imgs/logo';
import { useIsMobile } from '../../hooks/useIsMobile';
import './styles.css';

const BASE = '/adulto-idosos';

const adultoRoutes = [
  { label: 'Início', path: BASE },
  {
    label: 'Saúde Bucal',
    submenu: [
      { label: 'Higienização bucal', path: `${BASE}/saude-bucal/higienizacao-bucal` },
      { label: 'Próteses e implantes dentários', path: `${BASE}/saude-bucal/proteses-implantes` },
      { label: 'Agravos bucais', path: `${BASE}/saude-bucal/agravos-bucais` },
      { label: 'Saúde bucal e a gestação', path: `${BASE}/saude-bucal/gestacao` },
      { label: 'Saúde bucal e saúde geral', path: `${BASE}/saude-bucal/saude-geral` },
      { label: 'Saúde bucal e o envelhecimento', path: `${BASE}/saude-bucal/envelhecimento` },
    ],
  },
  {
    label: 'Saúde Geral',
    submenu: [
      { label: 'Hábitos saudáveis', path: `${BASE}/saude-geral/habitos-saudaveis` },
      { label: 'Saúde emocional', path: `${BASE}/saude-geral/saude-emocional` },
      { label: 'Saúde sexual', path: `${BASE}/saude-geral/saude-sexual` },
    ],
  },
  {
    label: 'Atividades',
    submenu: [
      { label: 'Quiz', path: `${BASE}/atividades/quiz` },
      { label: 'Cenários de simulação', path: `${BASE}/atividades/cenarios-simulacao` },
      { label: 'Mitos e Verdades', path: `${BASE}/atividades/mitos-verdades` },
    ],
  },
  { label: 'Quem somos', path: `${BASE}/quem-somos` },
  { label: 'Atendimento', path: `${BASE}/atendimento` },

];

const ctaRoute = { label: 'Fale Conosco', path: `${BASE}/fale-conosco` };

function AdultoDropdown({ label, options }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const closeTimer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const active = options.some((o) => location.pathname === o.path);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const openMenu = () => { cancelClose(); setOpen(true); };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  };

  const goTo = (path) => {
    cancelClose();
    setOpen(false);
    navigate(path);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      cancelClose();
    };
  }, []);

  return (
    <div
      ref={ref}
      className="ai-nav-item"
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
    >
      <button
        className={`ai-nav-link${active ? ' active' : ''}`}
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        {label}
        <ExpandMoreIcon style={{ fontSize: 18, marginLeft: 2 }} />
      </button>
      {open && (
        <ul className="ai-dropdown" onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
          {options.map((opt) => (
            <li
              key={opt.path}
              className="ai-dropdown-item"
              onClick={() => goTo(opt.path)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AdultoIdososHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);

  const toggleDrawer = (open) => () => setDrawerOpen(open);
  const handleNavigate = (path) => { navigate(path); setDrawerOpen(false); };

  const handleRedirect = () => {
    window.open('https://www.fo.ufu.br/contact', '_blank');
    window.location.href = '/';
  };

  return (
    <>
      {/* ---- Header desktop ---- */}
      {!isMobile && (
        <header className="ai-header">
          <div className="ai-brand" onClick={() => navigate(BASE)}>
            <Logo />
            <span className="ai-brand-name">@ViverSorrindo</span>
          </div>

          <nav className="ai-nav">
            {adultoRoutes.map((route) =>
              route.submenu ? (
                <AdultoDropdown key={route.label} label={route.label} options={route.submenu} />
              ) : (
                <button
                  key={route.label}
                  className={`ai-nav-link${location.pathname === route.path ? ' active' : ''}`}
                  onClick={() => navigate(route.path)}
                >
                  {route.label}
                </button>
              )
            )}
          </nav>

          <button className="ai-cta" onClick={() => handleRedirect()}>
            <ChatBubbleOutlineIcon style={{ fontSize: 18 }} />
            {ctaRoute.label}
          </button>
        </header>
      )}

      {/* ---- Header mobile ---- */}
      {isMobile && (
        <div className="ai-header ai-header-mobile">
          <div className="ai-brand" onClick={() => navigate(BASE)}>
            <Logo />
            <span className="ai-brand-name">@ViverSorrindo</span>
          </div>
          <IconButton onClick={toggleDrawer(true)} aria-label="Abrir menu">
            <MenuIcon />
          </IconButton>
        </div>
      )}

      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
        <div style={{ width: 280 }}>
          <div className="ai-drawer-head">
            <div className="ai-brand">
              <Logo />
              <span className="ai-brand-name" style={{ fontSize: '1.05rem' }}>@ViverSorrindo</span>
            </div>
            <IconButton onClick={toggleDrawer(false)} aria-label="Fechar menu">
              <CloseIcon />
            </IconButton>
          </div>
          <Divider />
          <List>
            {adultoRoutes.map((route) =>
              route.submenu ? (
                <div key={route.label}>
                  <ListItem button onClick={() => setOpenGroup(openGroup === route.label ? null : route.label)}>
                    <ListItemText primary={route.label} primaryTypographyProps={{ fontWeight: 700 }} />
                    <ExpandMoreIcon
                      style={{
                        transition: 'transform 0.2s',
                        transform: openGroup === route.label ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  </ListItem>
                  <Collapse in={openGroup === route.label} timeout="auto" unmountOnExit>
                    {route.submenu.map((sub) => (
                      <ListItem button key={sub.path} onClick={() => handleNavigate(sub.path)} sx={{ pl: 4 }}>
                        <ListItemText primary={sub.label} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                      </ListItem>
                    ))}
                  </Collapse>
                </div>
              ) : (
                <ListItem button key={route.path} onClick={() => handleNavigate(route.path)}>
                  <ListItemText primary={route.label} primaryTypographyProps={{ fontWeight: 700 }} />
                </ListItem>
              )
            )}
            <ListItem>
              <button className="ai-cta" style={{ width: '100%' }} onClick={() => handleNavigate(ctaRoute.path)}>
                <ChatBubbleOutlineIcon style={{ fontSize: 18 }} />
                {ctaRoute.label}
              </button>
            </ListItem>
          </List>
        </div>
      </Drawer>
    </>
  );
}

export default AdultoIdososHeader;
